import { type OrganizationManager } from "@entities";
import { Stack } from "@mui/material";
import { organizationManagerRepo } from "@repositories";
import { linko, type MethodResponse } from "@shared/linko";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { remult } from "remult";

export type UserProviderManager = MethodResponse<
  typeof linko.organizationManager.queries.getLoggedInManager
>;

interface UserContextType {
  currentUser: UserProviderManager | null;
  setCurrentUser: React.Dispatch<
    React.SetStateAction<UserProviderManager | null>
  >;
  updatedCurrentUser: <T extends keyof UserProviderManager>(
    a: T | UserProviderManager,
    b?: UserProviderManager[T],
  ) => Promise<void>;
  getUser: () => Promise<null | UserProviderManager>;
  logout: VoidFunction;
  login: () => Promise<void>;
}
const UserContext = createContext<UserContextType | null>(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error("user is not logged in!");
  } else {
    return context;
  }
};

export const UserProvider = ({ children }: { children: React.ReactNode; }) => {
  const [currentUser, setCurrentUser] = useState<UserProviderManager | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if the user is already logged in
    (async () => {
      await refetchUser();
      setLoading(false);
    })();
  }, []);

  const refetchUser = async () => {
    try {
      const { data: manager } =
        await linko.organizationManager.queries.getLoggedInManager();

      remult.user = {
        ...manager,
        roles: manager.roles as string[],
        organizationId: manager.organizationId,
        profileImageId: manager.profileImageId ?? undefined,
        memberId: manager.member?.id ?? manager.id,
        tableSource: "organization-manager",
      };

      setCurrentUser(manager);
      return manager;
    } catch (error) {
      // eslint-disable-next-line no-console -- error handling;
      console.error(error);
    }
    return null;
  };

  /**
   * Updates a specific field of the current user in the organization manager repository.
   *
   * @param whatToUpdate - The name of the field to be updated. This should be a valid key of the user object as string.
   * @param user - An object Partial<OrganizationManager>. `Value` is not needed if providing user instead of `whatToUpdate`;
   * @param value - The new value to be assigned to the specified field. The type is the type of the filed you entered to allow flexibility.
   * @returns void, the currentUser will be changed.
   *
   * @throws Will throw an error if the update operation fails or if `currentUser` is not defined.
   *
   * @example
   * // Update the user's email
   * await updatedCurrentUser('email', 'yotamHamelech@example.com');
   *
   * // Update the user's role
   * await updatedCurrentUser('role', 'admin');
   */
  async function updatedCurrentUser<T extends keyof OrganizationManager>(
    whatToUpdate: T,
    value: OrganizationManager[T],
  ): Promise<void>;
  async function updatedCurrentUser(
    user: Partial<OrganizationManager>,
  ): Promise<void>;
  async function updatedCurrentUser<T extends keyof OrganizationManager>(
    a: T | Partial<OrganizationManager>,
    b?: OrganizationManager[T],
  ) {
    if (typeof a === "object") {
      try {
        // Only update fields, not relations
        const updateData: Partial<OrganizationManager> = { ...a };
        // Remove relation fields that shouldn't be in update
        delete (updateData as any).organization;
        delete (updateData as any).member;
        delete (updateData as any).teams;
        delete (updateData as any).notifications;
        delete (updateData as any).phoneNumber;

        await organizationManagerRepo.update(remult.user!.id, updateData);
      } catch (error) {
        // eslint-disable-next-line no-console -- error handling;
        console.error(error);
        return;
      }
    } else {
      if (a === "phoneNumber") {
        return;
      }

      if (["roles", "name"].includes(a)) {
        (remult.user as unknown) = {
          ...remult.user,
          [a]: b,
        };
      }
      const updateData: Partial<OrganizationManager> = {
        [a]: b,
      } as Partial<OrganizationManager>;
      await organizationManagerRepo.update(
        remult.user!.id,
        updateData,
      );
    }

    refetchUser();
  }

  async function logout() {
    await linko.otp.admin.logout();
    remult.user = undefined;
    setCurrentUser(null);
    navigate("/login", { replace: true });
  }

  async function login() {
    await refetchUser();
  }

  if (loading) {
    return <Stack width="100vw" height="100vh" bgcolor="white" />;
  }
  return (
    <UserContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        updatedCurrentUser,
        getUser: refetchUser,
        logout,
        login,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
